<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/Plan.svelte"
  import Swiper from "swiper/bundle"
  import Modal from "$app/components/Modal.svelte"
  import { ChevronRight } from "lucide-svelte"
  import Course from "./Course.svelte"
  import LibraryVideo from "./LibraryVideo.svelte"
  import ViewVideo from "../Library/ViewVideo.svelte"
  import ViewBook from "../Books/ViewBook.svelte"
  import Book from "./Book.svelte"
  import PodcastEpisode from "./PodcastEpisode.svelte"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plan: PlanType
    admin_university: boolean
    courses: CourseType[]
    library_videos: LibraryVideoType[]
    initial_books: BookType[]
    plan_books: PlanBookType[]
    current_book: BookType | null
    course_progresses: CourseProgressType[]
    podcast_episodes: PodcastEpisodeType[]
    purchased_courses: CoursePurchaseType[]
    current_user: UserType
  }

  let props = $props()

  let {
    plan,
    admin_university = false,
    library_videos,
    plan_books,
    courses,
    course_progresses,
    podcast_episodes,
    purchased_courses,
    current_user
  }: Props = props

  let initial_books = $state(props.books || [])
  let current_book = $state(props.current_book || null)

  let books = initial_books.map((b) => {
    let plan_book = plan_books.find(function (pb) {
      if (pb.book_id === b.id) {
        return true
      }
    })

    return { ...b, plan_book: plan_book }
  })

  let current_plan_book = null

  let openVideoModal = $state(false)
  let openBookModal = $state(false)

  let book = $state(null)
  let library_video = $state(null)

  let course_images = {}
  courses.forEach(function (c, i) {
    course_images[c.id] = new URL(
      `/images/courses/e${c.id}_sm.jpg`,
      import.meta.url
    ).href
  })

  onMount(() => {
    appStore.current_user = current_user
    appStore.plan = plan
    appStore.current_book = current_book
    appStore.admin_university = admin_university

    if (current_book) {
      current_plan_book = plan_books.find(function (pb) {
        if (pb.book_id === current_book.id) {
          return true
        }
      })
    }
    if (current_plan_book) {
      current_book = { ...current_book, plan_book: current_plan_book }
    }

    const swiperVids = new Swiper(".video-library", {
      slidesPerView: 1,
      spaceBetween: 10,
      slidesPerGroup: 1,
      loop: true,
      // If we need pagination
      navigation: {
        nextEl: ".video-library .swiper-button-next",
        prevEl: ".video-library .swiper-button-prev"
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 10,
          slidesPerGroup: 2
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 10,
          slidesPerGroup: 3
        }
      }
    })

    const swiperPods = new Swiper(".podcast-slider", {
      slidesPerView: 1,
      spaceBetween: 10,
      slidesPerGroup: 1,
      loop: true,
      // If we need pagination
      navigation: {
        nextEl: ".podcast-slider .swiper-button-next",
        prevEl: ".podcast-slider .swiper-button-prev"
      },
      breakpoints: {
        768: {
          slidesPerView: 3,
          spaceBetween: 20,
          slidesPerGroup: 3
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 20,
          slidesPerGroup: 4
        }
      }
    })

    const swiperCourses = new Swiper(".course-slider", {
      slidesPerView: 1,
      spaceBetween: 10,
      slidesPerGroup: 1,
      loop: true,
      // If we need pagination
      navigation: {
        nextEl: ".course-slider .swiper-button-next",
        prevEl: ".course-slider .swiper-button-prev"
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '"></span>'
        }
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 10,
          slidesPerGroup: 2
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 10,
          slidesPerGroup: 3
        }
      }
    })

    const swiperBooks = new Swiper(".book-slider", {
      slidesPerView: 1,
      spaceBetween: 10,
      slidesPerGroup: 1,
      loop: true,
      // If we need pagination
      navigation: {
        nextEl: ".book-slider .swiper-button-next",
        prevEl: ".book-slider .swiper-button-prev"
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
        renderBullet: function (index, className) {
          return '<span class="' + className + '"></span>'
        }
      },
      breakpoints: {
        768: {
          slidesPerView: 2,
          spaceBetween: 10,
          slidesPerGroup: 2
        },
        1024: {
          slidesPerView: 4,
          spaceBetween: 10,
          slidesPerGroup: 4
        }
      }
    })
  })

  function viewVideo(data) {
    if (data) {
      library_video = data
      openVideoModal = true
    }
  }

  function viewBook(data) {
    if (data) {
      book = data
      openBookModal = true
    }
  }

  function updatePlanBook(event) {
    if (event.detail) {
      let plan_book = event.detail
      if (plan_book.status === "in_progress") {
        current_plan_book = plan_book

        current_book = books.find(function (b) {
          if (b.id === current_plan_book.book_id) {
            return true
          }
        })
        current_book = { ...current_book, plan_book: current_plan_book }
      }
    }
  }
</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css"
  />
</svelte:head>

<Layout {...props}>
  <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
    <section class="max-w-3xl mx-auto lg:max-w-7xl py-6">
      <h2
        class="text-2xl text-gray-900 sm:text-3xl flex items-center justify-between"
      >
        <a
          href="books"
          data-name="books-link"
          class="flex items-center gap-1 hover:border-b hover:border-black hover:font-bold"
        >
          Bookshelf
          <ChevronRight class="size-6" />
        </a>
        <a
          href="books"
          class="border border-blue-500 text-blue-600 hover:bg-blue-100 hover:underline py-2 px-4 text-lg flex items-center gap-1 shadow-lg"
        >
          View All Books
          <ChevronRight class="size-4" />
        </a>
      </h2>
      <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div>
          <p class="mt-1 truncate text-sm text-gray-500">Currently Reading</p>
          {#if current_book}
            <div class="py-2">
              <button
                onclick={() => {
                  book = current_book
                  openBookModal = true
                }}
                class="relative current-book hover:cursor-pointer hover:border-green-600 shadow-xl aspect-w-3 aspect-h-4 border-4 border-green-700 rounded"
              >
                <img
                  src={current_book.image_url}
                  class="object-cover"
                  alt={current_book.title}
                />
                <div class="absolute top-5 left-2 inline">
                  <span
                    class="bg-green-500 text-white text-sm rounded-lg py-1 px-2 font-bold z-10"
                    >Currently Reading</span
                  >
                </div>
              </button>
            </div>
          {:else}
            <div class="flex items-center h-full">
              <div
                class="border border-yellow-300 rounded bg-yellow-50 flex items-center py-4"
              >
                <div class="px-3">
                  <p
                    class="text-white text-lg font-bold p-2 bg-yellow-700 text-center rounded-lg mt-4"
                  >
                    Your plan is not currently reading a book
                  </p>
                  <a
                    href="books"
                    class="border rounded-lg text-center mt-6 border-yellow-500 text-yellow-800 hover:bg-yellow-50 bg-yellow-200 hover:text-yellow-700 py-2 px-4 text-base flex items-center hover:shadow-lg"
                  >
                    View Bookshelf and select a book to read
                  </a>
                </div>
              </div>
            </div>
          {/if}
        </div>
        <div class="md:col-span-3 col-span-1 lg:col-span-4">
          <p class="mt-1 truncate text-sm text-gray-500">Featured Books</p>
          <div class="netflix-slider">
            <div class="book-slider swiper">
              <div class="swiper-wrapper">
                {#each books as book (book.id)}
                  <Book {book} onview={viewBook} />
                {/each}
              </div>
              <div class="swiper-button-next"></div>
              <div class="swiper-button-prev"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section
      class="max-w-3xl mx-auto lg:max-w-7xl py-6 border-t border-indigo-200"
    >
      <h2
        class="text-2xl text-gray-900 sm:text-3xl flex items-center justify-between"
      >
        <a
          href="library"
          data-name="video-library-link"
          class="flex items-center gap-1 hover:border-b hover:border-black hover:font-bold"
        >
          Video Library

          <ChevronRight class="size-6" />
        </a>
        <a
          href="library"
          class="border border-blue-500 text-blue-600 hover:bg-blue-100 hover:underline py-2 px-4 text-lg flex items-center gap-1 shadow-lg"
        >
          View Video Library
          <ChevronRight class="size-4" />
        </a>
      </h2>
      <div class="netflix-slider">
        <p class="mt-1 truncate text-sm text-gray-500">Recommended Videos</p>
        <div class="video-library swiper">
          <div class="swiper-wrapper">
            {#each library_videos as library_video}
              <LibraryVideo {library_video} onViewVideo={viewVideo} />
            {/each}
          </div>
          <div class="swiper-button-next"></div>
          <div class="swiper-button-prev"></div>
        </div>
      </div>
    </section>

    <section
      class="max-w-3xl mx-auto lg:max-w-7xl py-6 border-t border-indigo-200"
    >
      <h2
        class="text-2xl text-gray-900 sm:text-3xl flex items-center justify-between"
      >
        <a
          href="courses"
          data-name="courses-link"
          class="flex items-center gap-1 hover:border-b hover:border-black hover:font-bold"
        >
          Courses
          <ChevronRight class="size-6" />
        </a>
        <a
          href="courses"
          class="border border-blue-500 text-blue-600 hover:bg-blue-100 hover:underline py-2 px-4 text-lg flex items-center gap-1 shadow-lg"
        >
          View All Courses
          <ChevronRight class="size-4" />
        </a>
      </h2>

      <div class="netflix-slider">
        <div class="course-slider swiper">
          <div class="swiper-wrapper">
            {#each courses as course}
              <Course {course} {course_progresses} {purchased_courses} />
            {/each}
          </div>
          <div class="swiper-button-next"></div>
          <div class="swiper-button-prev"></div>
        </div>
      </div>
    </section>

    <section
      class="max-w-3xl mx-auto lg:max-w-7xl py-6 border-t border-indigo-200"
    >
      <h2
        class="text-2xl text-gray-900 sm:text-3xl flex items-center justify-between"
      >
        <a
          href="podcast"
          data-name="podcast-link"
          class="flex items-center gap-1 hover:border-b hover:border-black hover:font-bold"
        >
          Surfacing Leaders Podcast
          <ChevronRight class="size-6" />
        </a>
        <a
          href="podcast"
          class="border border-blue-500 text-blue-600 hover:bg-blue-100 hover:underline py-2 px-4 text-lg flex items-center gap-1 shadow-lg"
        >
          View All Episodes
          <ChevronRight class="size-4" />
        </a>
      </h2>

      <div class="netflix-slider">
        <div class="podcast-slider swiper">
          <div class="swiper-wrapper">
            {#each podcast_episodes as podcast_episode (podcast_episode.id)}
              <PodcastEpisode {podcast_episode} />
            {/each}
          </div>
          <div class="swiper-button-next"></div>
          <div class="swiper-button-prev"></div>
        </div>
      </div>
    </section>
  </div>
</Layout>

<Modal
  bind:open={openVideoModal}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
  large={true}
>
  <ViewVideo {library_video} />
</Modal>

<Modal
  bind:open={openBookModal}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
  large={true}
>
  <ViewBook book_id={book.id} onplanbook={updatePlanBook} />
</Modal>

<style>
  .current-book {
    -webkit-transition: 250ms all;
    transition: 250ms all;
  }

  .current-book:hover {
    -webkit-transform: scale(1.05);
    transform: scale(1.05);
    z-index: 1;
  }

  .netflix-slider .swiper-wrapper {
    padding: 20px 0;
  }

  .netflix-slider .swiper-button-prev,
  .netflix-slider .swiper-button-next {
    background-color: black;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 30px;
    color: #fff !important;
    fill: #fff !important;
    stroke: #fff !important;
    -webkit-transition: 250ms all;
    transition: 250ms all;
    margin-left: -10px;
  }
  .netflix-slider .swiper-button-prev {
    left: 10px;
    margin-left: -10px;
  }
  .netflix-slider .swiper-button-next {
    right: 10px;
    margin-right: -10px;
  }
  .netflix-slider .swiper-button-prev:hover,
  .netflix-slider .swiper-button-next:hover {
    -webkit-transform: scale(1.6);
    transform: scale(1.6);
    z-index: 1;
  }
</style>
